import React, { useState } from 'react';
import { LuPencil, LuRefreshCw } from 'react-icons/lu';
import { construirUrlFoto } from '../../utils/construirUrlFoto';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import FotosEquipo from '../servicio/FotosEquipo';
import { subirFotosEquipo } from '../../utils/subirFoto';

// Fotos de cada equipo desde la ficha del trabajo (14-oct-2026): hasta 4, cada una con su rótulo (18-oct-2026).
// Sirve para el admin y para la foto que quedó en el celu: se cambian y se guardan al toque.
// Son las mismas casillas del cierre; salen en el PDF y en el informe de la empresa.
// 27-oct-2026: en un trabajo con fotos se ven primero para mirar (grandes, con su rótulo, se abren al
// tocarlas). Antes salía directo el editor de 4 casillas chicas con la cruz de borrar, y las que tardaban
// en cargar quedaban como cuadros grises. Para cambiarlas: "Editar fotos".
const verFoto = f => (String(f).startsWith('data:') ? f : construirUrlFoto(f));

function Foto({ foto, rotulo }) {
    const [intento, setIntento] = useState(0);
    const [estado, setEstado] = useState('cargando');   // cargando | ok | error
    const url = verFoto(foto);
    const src = intento && !String(url).startsWith('data:') ? `${url}${url.includes('?') ? '&' : '?'}r=${intento}` : url;
    return (
        <figure className="min-w-0">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-chip">
                {estado !== 'error' && (
                    <a href={url} target="_blank" rel="noreferrer" aria-label={`Abrir ${rotulo || 'foto'}`}>
                        <img src={src} alt={rotulo || 'Foto del equipo'} loading="lazy"
                            onLoad={() => setEstado('ok')} onError={() => setEstado('error')}
                            className={`w-full h-full object-cover transition-opacity ${estado === 'ok' ? 'opacity-100' : 'opacity-0'}`} />
                    </a>
                )}
                {estado === 'cargando' && <div className="absolute inset-0 animate-pulse bg-chip pointer-events-none" />}
                {estado === 'error' && (
                    <button type="button" onClick={() => { setEstado('cargando'); setIntento(i => i + 1); }}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-caption font-bold text-muted active:scale-95">
                        <LuRefreshCw size={16} /> No cargó · reintentar
                    </button>
                )}
            </div>
            <figcaption className="mt-1 px-0.5 text-caption font-bold text-secondary truncate">{rotulo || 'Sin rótulo'}</figcaption>
        </figure>
    );
}

function VerFotos({ fotos }) {
    const r = fotos.fotosRotulos || {};
    const lista = [[fotos.fotoAntes, r.antes], [fotos.fotoDespues, r.despues],
        ...(fotos.fotosExtra || []).map((f, i) => [f, (r.extra || [])[i]])].filter(([f]) => f);
    return (
        <div className="grid grid-cols-2 gap-2">
            {lista.map(([f, rot], i) => <Foto key={`${f}${i}`} foto={f} rotulo={rot} />)}
        </div>
    );
}

const sinEquipo = (it) => !it.equipoSerial || ['MOSTRADOR', 'SIN-SN'].includes(String(it.equipoSerial).toUpperCase());

function FotosDeUnEquipo({ servicioId, item, onCambio, presupuesto = false }) {
    const [fotos, setFotos] = useState({ fotoAntes: item.fotoAntes || null, fotoDespues: item.fotoDespues || null, fotosExtra: item.fotosExtra || [],
        fotosRotulos: item.fotosRotulos || null });
    const [guardando, setGuardando] = useState(false);
    const cambiar = async (c) => {
        const antes = fotos;
        const nuevas = { ...fotos, ...c };
        setFotos(nuevas);
        setGuardando(true);
        try {
            const { fallaron, ...subidas } = await subirFotosEquipo(nuevas, `s${servicioId}_${item.id}`);
            if (fallaron) throw new Error('subida');
            const r = await api.patch(`/servicios/${servicioId}/fotos`, { items: [{ id: item.id, ...subidas }] });
            const guardado = (r.data.items || []).find(x => x.id === item.id) || subidas;
            setFotos({ fotoAntes: guardado.fotoAntes, fotoDespues: guardado.fotoDespues, fotosExtra: guardado.fotosExtra || [],
                fotosRotulos: guardado.fotosRotulos || null });
            onCambio && onCambio(item.id, guardado);
            toast.success('Fotos guardadas');
        } catch (e) {
            setFotos(antes);
            toast.error(e?.response?.data?.mensaje || 'No se pudieron guardar las fotos. Revisá la señal.');
        } finally { setGuardando(false); }
    };
    const cuantas = [fotos.fotoAntes, fotos.fotoDespues, ...(fotos.fotosExtra || [])].filter(Boolean).length;
    const [editar, setEditar] = useState(presupuesto || !cuantas);
    return (
        <div className="p-3.5 rounded-2xl bg-panel">
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <p className="text-body font-black text-brand-red truncate">{item.equipoSerial}</p>
                <span className="flex items-center gap-2 shrink-0">
                    <span className="text-caption font-bold text-muted">{guardando ? 'Guardando…' : cuantas ? `${cuantas} foto${cuantas !== 1 ? 's' : ''}` : 'Sin fotos'}</span>
                    {cuantas > 0 && !presupuesto && (
                        <button type="button" onClick={() => setEditar(e => !e)}
                            className="h-8 px-2.5 rounded-lg bg-chip text-ink text-caption font-black inline-flex items-center gap-1 active:scale-95">
                            {editar ? 'Listo' : <><LuPencil size={12} /> Editar fotos</>}
                        </button>
                    )}
                </span>
            </div>
            {item.equipoUbicacion && item.equipoUbicacion !== 'MOSTRADOR' && <p className="-mt-1.5 mb-2 text-caption text-muted truncate">{item.equipoUbicacion}</p>}
            {editar ? <FotosEquipo {...fotos} onChange={cambiar} disabled={guardando} /> : <VerFotos fotos={fotos} />}
        </div>
    );
}

// presupuesto (16-oct-2026): todavía no se fue; las fotos que mandó el cliente pasan al trabajo con su rótulo
export default function FotosEquiposDelTrabajo({ servicio, onCambio, presupuesto = false }) {
    const items = (servicio.items || []).filter(it => it.id && !sinEquipo(it));
    if (!items.length) return null;
    return (
        <div className="space-y-2">
            <p className="px-1 text-caption font-bold text-muted">Fotos {items.length === 1 ? 'del equipo' : `de los ${items.length} equipos`}</p>
            {presupuesto && <p className="px-1 -mt-1 text-caption text-muted">¿El cliente te mandó fotos? Cargalas acá con su rótulo (ej. "Antes · frente"): pasan al trabajo y al cerrar sumás las tuyas.</p>}
            {items.map(it => <FotosDeUnEquipo key={it.id} servicioId={servicio.id} item={it} onCambio={onCambio} presupuesto={presupuesto} />)}
        </div>
    );
}
